import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Upload, FileText, Download, AlertCircle, Check, X,
  FileSpreadsheet, Table, Eye, Trash2, RefreshCw
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const DEFAULT_CATEGORIES = [
  'Rent', 'Electricity', 'Internet/Phone', 'Salaries & Advances', 'Transportation',
  'Fuel', 'Maintenance', 'Office Supplies', 'Packaging', 'Marketing',
  'Professional Fees', 'Insurance', 'Bank Fees', 'Software', 'Travel',
  'Meals', 'Raw Material', 'Other'
];

const COLUMN_MAPPINGS = {
  date: ['date', 'expense date', 'transaction date', 'txn date'],
  category: ['category', 'expense category', 'type', 'expense type'],
  amount: ['amount', 'expense amount', 'total', 'value', 'cost'],
  payment_mode: ['payment mode', 'mode', 'payment method', 'method'],
  vendor_name: ['vendor', 'vendor name', 'supplier', 'paid to'],
  reference_no: ['reference', 'ref', 'reference no', 'invoice no', 'bill no'],
  notes: ['notes', 'description', 'remarks', 'memo'],
};

const ImportExpensesPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1); // 1: Upload, 2: Map Columns, 3: Preview, 4: Importing
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState({ success: 0, failed: 0, errors: [] });

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const ext = uploadedFile.name.split('.').pop().toLowerCase();

    if (!validTypes.includes(uploadedFile.type) && !['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setFile(uploadedFile);
    parseFile(uploadedFile);
  };

  const parseFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    
    if (ext === 'csv') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          toast.error('File must have headers and at least one data row');
          return;
        }

        const headerRow = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        setHeaders(headerRow);
        
        const data = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row = {};
          headerRow.forEach((h, i) => {
            row[h] = values[i] || '';
          });
          return row;
        });
        
        setRawData(data);
        autoMapColumns(headerRow);
        setStep(2);
      };
      reader.readAsText(file);
    } else {
      // For Excel files, we'd need a library like xlsx
      toast.error('Excel import requires additional setup. Please use CSV for now.');
      // Placeholder for xlsx support
    }
  };

  const autoMapColumns = (headers) => {
    const newMap = {};
    
    Object.keys(COLUMN_MAPPINGS).forEach(field => {
      const possibleNames = COLUMN_MAPPINGS[field];
      const matchedHeader = headers.find(h => 
        possibleNames.includes(h.toLowerCase())
      );
      if (matchedHeader) {
        newMap[field] = matchedHeader;
      }
    });
    
    setColumnMap(newMap);
  };

  const generatePreview = () => {
    const preview = rawData.map((row, idx) => {
      const mapped = {
        _rowNum: idx + 1,
        _isValid: true,
        _errors: [],
      };

      // Map fields
      Object.keys(COLUMN_MAPPINGS).forEach(field => {
        const sourceCol = columnMap[field];
        mapped[field] = sourceCol ? row[sourceCol] : '';
      });

      // Validate
      if (!mapped.category) {
        mapped._isValid = false;
        mapped._errors.push('Missing category');
      }
      if (!mapped.amount || isNaN(parseFloat(mapped.amount)) || parseFloat(mapped.amount) <= 0) {
        mapped._isValid = false;
        mapped._errors.push('Invalid amount');
      }
      if (!mapped.date) {
        mapped.date = new Date().toISOString().split('T')[0];
      } else {
        // Try to parse date
        const parsed = new Date(mapped.date);
        if (isNaN(parsed.getTime())) {
          mapped._errors.push('Invalid date format');
          mapped.date = new Date().toISOString().split('T')[0];
        } else {
          mapped.date = parsed.toISOString().split('T')[0];
        }
      }

      // Default payment mode
      if (!mapped.payment_mode || !['CASH', 'UPI', 'BANK', 'CARD', 'CHEQUE'].includes(mapped.payment_mode.toUpperCase())) {
        mapped.payment_mode = 'CASH';
      } else {
        mapped.payment_mode = mapped.payment_mode.toUpperCase();
      }

      return mapped;
    });

    setPreviewData(preview);
    setStep(3);
  };

  const handleImport = async () => {
    const validRows = previewData.filter(r => r._isValid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    setStep(4);

    const results = { success: 0, failed: 0, errors: [] };

    for (const row of validRows) {
      try {
        const payload = {
          date: row.date,
          category: row.category,
          amount: parseFloat(row.amount),
          payment_mode: row.payment_mode,
          reference_no: row.reference_no || null,
          notes: row.notes || null,
        };

        await api.post('/expenses', payload);
        results.success++;
      } catch (e) {
        results.failed++;
        results.errors.push(`Row ${row._rowNum}: ${e.response?.data?.detail || e.message}`);
      }
    }

    setImportResults(results);
    setImporting(false);

    if (results.success > 0) {
      toast.success(`${results.success} expense(s) imported successfully`);
    }
    if (results.failed > 0) {
      toast.error(`${results.failed} expense(s) failed to import`);
    }
  };

  const downloadTemplate = () => {
    const headers = ['date', 'category', 'amount', 'payment_mode', 'vendor_name', 'reference_no', 'notes'];
    const sampleData = [
      ['2024-01-15', 'Rent', '15000', 'BANK', 'ABC Properties', 'INV-001', 'Monthly office rent'],
      ['2024-01-16', 'Electricity', '2500', 'UPI', 'State Electricity', 'BILL-123', 'January bill'],
      ['2024-01-17', 'Office Supplies', '800', 'CASH', 'Staples Store', '', 'Stationery'],
    ];

    const csv = [headers.join(','), ...sampleData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'expenses_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStep(1);
    setFile(null);
    setRawData([]);
    setHeaders([]);
    setColumnMap({});
    setPreviewData([]);
    setImportResults({ success: 0, failed: 0, errors: [] });
  };

  const validCount = previewData.filter(r => r._isValid).length;
  const invalidCount = previewData.filter(r => !r._isValid).length;

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-4 shadow-sm">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Import Expenses</h1>
              <p className="text-sm text-slate-500">Import expenses from CSV or Excel file</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={downloadTemplate}
              className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Template
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                step >= s ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 4 && <div className={`w-24 h-1 mx-2 ${step > s ? 'bg-red-600' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>
        <div className="flex items-center justify-center gap-16 mb-8 text-xs text-slate-500">
          <span className={step === 1 ? 'text-red-600 font-medium' : ''}>Upload File</span>
          <span className={step === 2 ? 'text-red-600 font-medium' : ''}>Map Columns</span>
          <span className={step === 3 ? 'text-red-600 font-medium' : ''}>Preview</span>
          <span className={step === 4 ? 'text-red-600 font-medium' : ''}>Import</span>
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-sm p-8 border border-slate-200">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <FileSpreadsheet className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Upload Your File</h2>
              <p className="text-slate-500 mb-6">Upload a CSV or Excel file with your expense data</p>

              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-xl p-10 hover:border-red-400 hover:bg-red-50/30 transition-colors cursor-pointer mb-6"
              >
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-sm font-medium text-slate-700">Click to upload or drag and drop</p>
                <p className="text-xs text-slate-500 mt-2">CSV, XLS, XLSX (max 10MB)</p>
              </div>
              <input 
                ref={fileInputRef}
                type="file" 
                accept=".csv,.xls,.xlsx"
                onChange={handleFileUpload}
                className="hidden" 
              />

              <div className="flex items-center justify-center gap-4 text-sm">
                <button onClick={downloadTemplate} className="text-red-600 hover:underline flex items-center gap-1">
                  <Download className="w-4 h-4" /> Download sample template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Map Columns */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Map Columns</h2>
                <p className="text-sm text-slate-500">Match your file columns to expense fields</p>
              </div>
              <p className="text-sm text-slate-600">
                <FileText className="w-4 h-4 inline mr-1" />
                {file?.name} ({rawData.length} rows)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {Object.keys(COLUMN_MAPPINGS).map(field => (
                <div key={field} className="flex items-center gap-3">
                  <label className="w-32 text-sm font-medium text-slate-700 capitalize">
                    {field.replace('_', ' ')}
                    {['category', 'amount'].includes(field) && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={columnMap[field] || ''}
                    onChange={(e) => setColumnMap({ ...columnMap, [field]: e.target.value })}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="">-- Select column --</option>
                    {headers.map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <button onClick={reset} className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg text-sm font-medium">
                Start Over
              </button>
              <button 
                onClick={generatePreview}
                disabled={!columnMap.category || !columnMap.amount}
                className="px-5 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                <Eye className="w-4 h-4" /> Preview Import
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && (
          <div className="space-y-5">
            {/* Summary */}
            <div className="flex items-center gap-4">
              <div className="flex-1 bg-white rounded-xl shadow-sm p-4 border border-slate-200 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Valid Rows</p>
                  <p className="text-lg font-bold text-emerald-600">{validCount}</p>
                </div>
              </div>
              {invalidCount > 0 && (
                <div className="flex-1 bg-white rounded-xl shadow-sm p-4 border border-slate-200 flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Invalid Rows (will be skipped)</p>
                    <p className="text-lg font-bold text-red-600">{invalidCount}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Preview Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full min-w-[900px]">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">#</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Status</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Category</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600">Amount</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Mode</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, idx) => (
                      <tr key={idx} className={`border-b border-slate-100 ${row._isValid ? 'bg-white' : 'bg-red-50'}`}>
                        <td className="px-3 py-2 text-sm text-slate-500">{row._rowNum}</td>
                        <td className="px-3 py-2">
                          {row._isValid ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                              <Check className="w-3 h-3" /> Valid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" title={row._errors.join(', ')}>
                              <X className="w-3 h-3" /> {row._errors[0]}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm">{row.date}</td>
                        <td className="px-3 py-2 text-sm">{row.category || '—'}</td>
                        <td className="px-3 py-2 text-sm text-right font-medium">₹{parseFloat(row.amount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-sm">{row.payment_mode}</td>
                        <td className="px-3 py-2 text-sm text-slate-500 truncate max-w-[200px]">{row.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button onClick={() => setStep(2)} className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg text-sm font-medium">
                Back to Mapping
              </button>
              <button 
                onClick={handleImport}
                disabled={validCount === 0}
                className="px-5 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> Import {validCount} Expense{validCount !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Import Results */}
        {step === 4 && (
          <div className="bg-white rounded-xl shadow-sm p-8 border border-slate-200">
            <div className="max-w-md mx-auto text-center">
              {importing ? (
                <>
                  <div className="w-16 h-16 border-4 border-slate-200 border-t-red-600 rounded-full animate-spin mx-auto mb-6"></div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-2">Importing Expenses...</h2>
                  <p className="text-slate-500">Please wait while we import your data</p>
                </>
              ) : (
                <>
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
                    importResults.failed === 0 ? 'bg-emerald-100' : 'bg-amber-100'
                  }`}>
                    {importResults.failed === 0 ? (
                      <Check className="w-10 h-10 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-10 h-10 text-amber-600" />
                    )}
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">Import Complete</h2>
                  <p className="text-slate-500 mb-6">
                    Successfully imported {importResults.success} expense{importResults.success !== 1 ? 's' : ''}
                    {importResults.failed > 0 && `, ${importResults.failed} failed`}
                  </p>

                  <div className="flex items-center justify-center gap-3">
                    <button onClick={reset} className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg text-sm font-medium flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" /> Import More
                    </button>
                    <button 
                      onClick={() => navigate(`/business/${bid}/expenses`)}
                      className="px-5 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium"
                    >
                      View Expenses
                    </button>
                  </div>

                  {importResults.errors.length > 0 && (
                    <div className="mt-6 p-4 bg-red-50 rounded-lg text-left">
                      <p className="text-sm font-medium text-red-800 mb-2">Errors:</p>
                      <ul className="text-xs text-red-700 space-y-1">
                        {importResults.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                        {importResults.errors.length > 5 && (
                          <li>• ...and {importResults.errors.length - 5} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportExpensesPage;
